import { ONYX_APP_HEADER, ZION_API_BASE } from './ZionServer.ts'

export interface ZionAuthBody {
  accountId: string
  nickname: string
  password: string
  publicKey: string
  app: 'zion'
}

export interface ZionLoginResult {
  token?: string
  nickname?: string
  requiresTotp?: boolean
  error?: string
}

export interface ZionRegisterResult {
  token?: string
  nickname?: string
  accountId?: string
  error?: string
}

export class ZionApiError extends Error {
  status: number
  body?: string

  constructor(message: string, status: number, body?: string) {
    super(message)
    this.name = 'ZionApiError'
    this.status = status
    this.body = body
  }
}

function defaultHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-ONYX-App': ONYX_APP_HEADER,
  }
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

async function zionFetch<T>(
  path: string,
  init: RequestInit = {},
  token?: string,
): Promise<T> {
  const url = `${ZION_API_BASE}${path}`
  const response = await fetch(url, {
    ...init,
    headers: {
      ...defaultHeaders(token),
      ...(init.headers as Record<string, string> | undefined),
    },
  })

  const text = await response.text()
  let data: T
  try {
    data = text ? (JSON.parse(text) as T) : ({} as T)
  } catch {
    if (!response.ok) {
      throw new ZionApiError(text || response.statusText, response.status, text)
    }
    throw new ZionApiError('Risposta non JSON dal server', response.status, text)
  }

  if (!response.ok) {
    const err = (data as { error?: string }).error ?? response.statusText
    throw new ZionApiError(err, response.status, text)
  }
  return data
}

export async function checkNicknameAvailable(nickname: string): Promise<boolean> {
  const q = encodeURIComponent(nickname.trim())
  const paths = [
    `/zion/auth/check-nickname?nickname=${q}`,
    `/zion/auth/nick-available?nick=${q}`,
  ]
  for (const path of paths) {
    try {
      const data = await zionFetch<{ available?: boolean; ok?: boolean }>(path, { method: 'GET' })
      if (typeof data.available === 'boolean') return data.available
      if (typeof data.ok === 'boolean') return data.ok
    } catch {
      continue
    }
  }
  return false
}

export async function register(
  password: string,
  publicKey: string,
  acceptedPrivacy = true,
  acceptedTerms = true,
): Promise<ZionRegisterResult> {
  const body = {
    password,
    passwordConfirm: password,
    publicKey,
    app: 'zion' as const,
    acceptedPrivacy,
    acceptedTerms,
  }
  return zionFetch<ZionRegisterResult>('/zion/auth/register', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function login(
  nickname: string,
  password: string,
  publicKey: string,
): Promise<ZionLoginResult> {
  const body: ZionAuthBody = {
    accountId: nickname.trim().toLowerCase(),
    nickname: nickname.trim().toLowerCase(),
    password,
    publicKey,
    app: 'zion',
  }
  return zionFetch<ZionLoginResult>('/zion/auth/login', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Lookup chiave pubblica sul server.
 * Zero-Knowledge: spesso 404/410 — un solo path primario, stop immediato su not-found.
 * Niente loop di fallback URL (rumore console + ritardi).
 */
export async function getPublicKey(nickname: string, token?: string): Promise<string | null> {
  const raw = nickname.trim().toLowerCase()
  if (!raw) return null

  const encoded = encodeURIComponent(raw)
  // Un solo endpoint canonico — stop su 404/410 senza alternative
  const path = `/zion/publickey/${encoded}`

  try {
    const data = await zionFetch<{ publicKey?: string }>(path, { method: 'GET' }, token)
    const key = data.publicKey?.trim()
    return key || null
  } catch (e) {
    if (e instanceof ZionApiError && (e.status === 404 || e.status === 410)) {
      return null
    }
    // Secondo path legacy solo se non è 404/410 (es. 502 sul primo)
    try {
      const data = await zionFetch<{ publicKey?: string }>(
        `/publickey/${encoded}`,
        { method: 'GET' },
        token,
      )
      const key = data.publicKey?.trim()
      return key || null
    } catch (e2) {
      if (e2 instanceof ZionApiError && (e2.status === 404 || e2.status === 410)) {
        return null
      }
      return null
    }
  }
}

export interface SearchUserEntry {
  nickname: string
  displayName?: string
}

export async function searchUsersDetailed(query: string, token: string): Promise<SearchUserEntry[]> {
  const q = encodeURIComponent(query.trim().toLowerCase())
  const data = await zionFetch<{ users?: unknown[]; results?: unknown[] }>(
    `/zion/users/search?q=${q}`,
    { method: 'GET' },
    token,
  )
  const raw = data.users ?? data.results ?? []
  return raw
    .map((item) => {
      if (typeof item === 'string') return { nickname: item.trim().toLowerCase() }
      if (item && typeof item === 'object') {
        const o = item as { nickname?: string; nick?: string; display_name?: string }
        const nick = (o.nickname ?? o.nick ?? '').trim().toLowerCase()
        if (!nick) return null
        return { nickname: nick, displayName: o.display_name?.trim() }
      }
      return null
    })
    .filter((x): x is SearchUserEntry => !!x)
}

export async function fetchPeerDailyCodes(
  token: string,
  accountIds: string[],
): Promise<Record<string, string>> {
  if (!accountIds.length) return {}
  const body = { accountIds: accountIds.map((id) => id.trim().toLowerCase()).filter(Boolean) }
  const data = await zionFetch<{ codes?: Record<string, string> }>(
    '/zion/identity/daily/batch',
    { method: 'POST', body: JSON.stringify(body) },
    token,
  )
  return data.codes ?? {}
}

export async function fetchMyDailyCode(token: string): Promise<string | null> {
  const data = await zionFetch<{ dailyCode?: string }>('/zion/identity/daily', { method: 'GET' }, token)
  return data.dailyCode?.trim().toUpperCase() ?? null
}

export async function getAppVersion(): Promise<string> {
  const data = await zionFetch<{ version?: string }>('/zion/app/version', { method: 'GET' })
  return data.version ?? 'unknown'
}
